import React, { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ReactSelect from "react-select";
import AsyncSelect from "react-select/async";
import { ArrowPathIcon, LinkIcon, UserMinusIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import CustomDialog from "../modals/CustomDialog/CustomDialog";
import { Button } from "../forms/Buttons/Button";
import { defaultSelectStyles, selectTheme } from "../forms/Select/styles";
import {
  getFolderAccess,
  getFolderCandidates,
  removeFolderMember,
  setFolderLink,
  setFolderMember,
} from "../../services/questionService";
import { getApiErrorMessage } from "../../utils/apiError";
import { isCanceledError } from "../../utils/examError";
import { ROLES } from "../../utils/folders";
import { UserContext } from "../../utils/contexts/UserContext";

// The roles "Anyone with the link" can give: a moderator is only named by an admin.
const LINK_ROLES = ["viewer", "author", "expert"];
const SEARCH_DELAY = 300;
// The last item of a member's role menu: it takes the person's access away (no separate button on each row).
const REMOVE = "remove";

const focusRing =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";

const fullName = (user) =>
  [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.username || "";

// "login · e-mail", without the login when it already is the shown name.
const accountLine = (user) =>
  [user?.username !== fullName(user) && user?.username, user?.email].filter(Boolean).join(" · ");

const initials = (user) => {
  const first = Array.from(user?.first_name?.trim() || "")[0] || "";
  const last = Array.from(user?.last_name?.trim() || "")[0] || "";
  return (first + last || Array.from(user?.username || "?").slice(0, 2).join("")).toUpperCase();
};

const normalizeAccess = (data) => ({
  members: Array.isArray(data?.members) ? data.members : [],
  link: { role: data?.link?.role ?? null, token: data?.link?.token ?? null },
  canGrantModerator: !!data?.can_grant_moderator,
});

const toCandidateOption = (user) => ({ value: user.uuid, label: fullName(user), user });

// A role select option, with the line on what the role allows.
const roleOption = (t, role) => ({
  value: role,
  label: t(`folders.roles.${role}`),
  hint: t(`folders.share.role_hint.${role}`),
});

// The selects are 44px tall on phones.
const phoneControl = (provided, state) => ({
  ...defaultSelectStyles.control(provided, state),
  "@media (max-width: 639px)": { minHeight: "2.75rem" },
});
const searchSelectStyles = { ...defaultSelectStyles, control: phoneControl };

// Role menus open to the left so a wide menu stays inside the dialog when the select sits at the row's right edge.
// "Remove access" is red, under a line.
const roleSelectStyles = {
  ...defaultSelectStyles,
  control: phoneControl,
  menu: (provided, state) => ({
    ...defaultSelectStyles.menu(provided, state),
    left: "auto",
    right: 0,
    width: "max-content",
    minWidth: "100%",
    maxWidth: "min(20rem, calc(100vw - 2rem))",
  }),
  option: (provided, state) =>
    state.data.danger
      ? {
          ...defaultSelectStyles.option(provided, state),
          position: "relative",
          marginTop: "0.5rem",
          color: "#dc2626",
          backgroundColor: state.isFocused ? "#fef2f2" : "transparent",
          "&:active": { backgroundColor: "#fee2e2" },
          "&::before": { content: '""', position: "absolute", left: "0.5rem", right: "0.5rem", top: "-0.25rem", height: "1px", backgroundColor: "#f1f5f9" },
        }
      : defaultSelectStyles.option(provided, state),
};

const formatRoleOption = (option, { context }) =>
  context === "menu" && option.danger ? (
    <span className="flex items-center gap-2 py-0.5 font-medium">
      <UserMinusIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {option.label}
    </span>
  ) : context === "menu" && option.hint ? (
    <span className="block py-0.5">
      <span className="block font-medium">{option.label}</span>
      <span className="mt-0.5 block text-xs leading-snug opacity-75">{option.hint}</span>
    </span>
  ) : (
    option.label
  );

function Spinner({ className = "h-5 w-5" }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" className="opacity-30" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// The person's initials on a quiet grey circle (colour is kept for what means something, e.g. "via link").
function Avatar({ user, small = false }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-slate-100 font-semibold text-slate-600 ${
        small ? "h-7 w-7 text-[11px]" : "h-9 w-9 text-sm"
      }`}
    >
      {initials(user)}
    </span>
  );
}

// A react-select of roles; `options` are {value, label, hint}. Tab only leaves it: react-select would otherwise
// pick the highlighted option on Tab (a role, "Remove access" or the link's "Off").
function RoleSelect({ inputId, ariaLabel, options, value, onChange, isDisabled, isLoading, className = "" }) {
  const [t] = useTranslation();
  return (
    <div className={className}>
      <ReactSelect
        inputId={inputId}
        aria-label={ariaLabel}
        styles={roleSelectStyles}
        theme={selectTheme}
        options={options}
        value={options.find((o) => o.value === value) || null}
        onChange={(o) => o && onChange(o.value)}
        formatOptionLabel={formatRoleOption}
        tabSelectsValue={false}
        isSearchable={false}
        isDisabled={isDisabled}
        isLoading={isLoading}
        menuPlacement="auto"
        placeholder={t("buttons.select")}
        noOptionsMessage={() => t("table.no_options")}
      />
    </div>
  );
}

/**
 * "Share" for a subject folder (an exam's subfolder), for its moderators and admins: who has it and with
 * which role, adding people, and "Anyone with the link". Every change is saved at once; the list comes
 * from the server's answers. Only an admin (can_grant_moderator) names, changes or removes a moderator.
 * `folder` is a question_folders list item, `examName` the name of its exam (for the title).
 */
export default function ShareFolderDialog({ isOpen, folder, examName, onClose }) {
  const [t] = useTranslation();
  const me = useContext(UserContext)?.user;
  const id = useId();
  const folderUuid = folder?.uuid || "";

  const [access, setAccess] = useState(null); // normalizeAccess(), or null until loaded
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null); // the axios error
  // Requests in flight: a member's uuid, "add" or "link" -> true. Their controls wait for the answer.
  const [pending, setPending] = useState({});
  // The last failed change: {scope: a member's uuid | "add" | "link", err, fallbackKey}. Shown next to its controls.
  const [error, setError] = useState(null);
  const [candidate, setCandidate] = useState(null); // the picked AsyncSelect option
  const [newRole, setNewRole] = useState("author");
  const [searchFailed, setSearchFailed] = useState(false);
  // Remounts the search after an add, so its suggestions are loaded again without the added person.
  const [searchKey, setSearchKey] = useState(0);
  const [manualLink, setManualLink] = useState(""); // shown when copying to the clipboard failed

  // The folder whose answers may still be applied: one from a closed dialog or another folder is dropped.
  const activeRef = useRef(null);
  const loadAbortRef = useRef(null);
  const searchTimerRef = useRef(null);
  const searchAbortRef = useRef(null);
  const suggestAbortRef = useRef(null);
  const bodyRef = useRef(null);
  const membersRef = useRef(null);
  // A change being saved ({uuid, next}: the person's and the next person's, or "add"): its control is off meanwhile,
  // which drops the focus, so it comes back to the person's select after, or to the next person's once they are
  // removed, else to the search (after an add too, for the next person).
  const refocusRef = useRef(null);

  // Every opening starts from scratch. Reset while the opening renders, so the previous folder's people
  // never flash; not on close, so the content does not change while the panel fades out.
  const [resetFor, setResetFor] = useState(null);
  const openedFor = isOpen && folderUuid ? folderUuid : null;
  if (openedFor !== resetFor) {
    setResetFor(openedFor);
    if (openedFor !== null) {
      setAccess(null);
      setLoading(true);
      setLoadError(null);
      setPending({});
      setError(null);
      setCandidate(null);
      setNewRole("author");
      setSearchFailed(false);
      setSearchKey((k) => k + 1);
      setManualLink("");
    }
  }

  // `quiet` refreshes the list after a failed change without the spinner and without a load error.
  const loadAccess = useCallback((uuid, { quiet = false } = {}) => {
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    if (!quiet) {
      setLoading(true);
      setLoadError(null);
    }
    getFolderAccess(uuid, { signal: controller.signal })
      .then((data) => {
        if (activeRef.current !== uuid) return;
        setAccess(normalizeAccess(data));
        setLoadError(null);
      })
      .catch((err) => {
        if (isCanceledError(err) || activeRef.current !== uuid || quiet) return;
        setLoadError(err);
      })
      .finally(() => {
        if (loadAbortRef.current !== controller) return;
        loadAbortRef.current = null;
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!isOpen || !folderUuid) return undefined;
    activeRef.current = folderUuid;
    loadAccess(folderUuid);
    return () => {
      activeRef.current = null;
      loadAbortRef.current?.abort();
      clearTimeout(searchTimerRef.current);
      searchAbortRef.current?.abort();
      suggestAbortRef.current?.abort();
    };
  }, [isOpen, folderUuid, loadAccess]);

  // The suggestions on focus (empty input) load at once; typed searches wait for a pause and replace
  // the previous one.
  const loadCandidates = useCallback(
    (input, callback) => {
      const search = input.trim();
      const fetchInto = (controller) =>
        getFolderCandidates(folderUuid, search, { signal: controller.signal })
          .then((data) => {
            setSearchFailed(false);
            callback((Array.isArray(data) ? data : data?.results || []).map(toCandidateOption));
          })
          .catch((err) => {
            if (isCanceledError(err)) return;
            setSearchFailed(true);
            callback([]);
          });
      if (!input) {
        suggestAbortRef.current?.abort();
        suggestAbortRef.current = new AbortController();
        fetchInto(suggestAbortRef.current);
        return;
      }
      clearTimeout(searchTimerRef.current);
      searchAbortRef.current?.abort();
      searchTimerRef.current = setTimeout(() => {
        searchAbortRef.current = new AbortController();
        fetchInto(searchAbortRef.current);
      }, SEARCH_DELAY);
    },
    [folderUuid]
  );

  const busy = Object.keys(pending).length > 0;

  // Sends one change; `apply(data)` takes the answer. A 404 (the person or the link changed meanwhile)
  // also refreshes the list.
  const run = async (scope, request, apply, fallbackKey) => {
    const uuid = folderUuid;
    setPending((p) => ({ ...p, [scope]: true }));
    setError(null);
    try {
      const data = await request();
      if (activeRef.current === uuid) apply(data);
    } catch (err) {
      if (activeRef.current !== uuid) return;
      setError({ scope, err, fallbackKey });
      if (err?.response?.status === 404) loadAccess(uuid, { quiet: true });
    } finally {
      setPending((p) => {
        const next = { ...p };
        delete next[scope];
        return next;
      });
    }
  };

  // The member from POST members/: replaces the person's row, or is added at the end (the server's order).
  const applyMember = (member) => {
    const userUuid = member?.user?.uuid;
    if (!userUuid) {
      loadAccess(folderUuid, { quiet: true });
      return;
    }
    setAccess((a) => {
      const exists = a.members.some((m) => m.user?.uuid === userUuid);
      return {
        ...a,
        members: exists ? a.members.map((m) => (m.user?.uuid === userUuid ? member : m)) : [...a.members, member],
      };
    });
  };

  const handleAdd = () => {
    if (!candidate || pending.add) return;
    refocusRef.current = { uuid: "add", next: null };
    run(
      "add",
      () => setFolderMember(folderUuid, candidate.value, newRole),
      (member) => {
        applyMember(member);
        setCandidate(null);
        setSearchKey((k) => k + 1);
      },
      "folders.share.add_failed"
    );
  };

  // Where the focus goes once `uuid`'s change is saved (refocusRef): the next person in the list, else the previous one.
  const keepFocus = (uuid) => {
    const members = access?.members || [];
    const index = members.findIndex((m) => m.user?.uuid === uuid);
    refocusRef.current = { uuid, next: (members[index + 1] || members[index - 1])?.user?.uuid || null };
  };

  const handleRoleChange = (member, role) => {
    if (role === member.role) return;
    keepFocus(member.user.uuid);
    run(member.user.uuid, () => setFolderMember(folderUuid, member.user.uuid, role), applyMember, "folders.share.role_failed");
  };

  // "Remove access" sits in the person's role menu, next to the roles: it asks first.
  const handleRemove = (member) => {
    const userUuid = member.user.uuid;
    if (!window.confirm(t("folders.share.remove_confirm", { name: fullName(member.user) }))) return;
    keepFocus(userUuid);
    run(
      userUuid,
      () => removeFolderMember(folderUuid, userUuid),
      () => setAccess((a) => ({ ...a, members: a.members.filter((m) => m.user?.uuid !== userUuid) })),
      "folders.share.remove_failed"
    );
  };

  // A saved (or failed) change gives the focus back, unless the user has gone on elsewhere meanwhile.
  useEffect(() => {
    const target = refocusRef.current;
    if (!target || pending[target.uuid]) return;
    refocusRef.current = null;
    // Lost: on the page, or on a control that is off or gone.
    const active = document.activeElement;
    if (active && active !== document.body && !active.disabled && active.isConnected) return;
    const select = (uuid) => uuid && membersRef.current?.querySelector(`[data-member="${uuid}"] input:not([disabled])`);
    (select(target.uuid) || select(target.next) || document.getElementById(`${id}-search`))?.focus();
  }, [pending, id]);

  const viaLinkCount = access ? access.members.filter((m) => m.via_link).length : 0;

  const handleLinkChange = (value) => {
    const role = value || null;
    if (!access || role === access.link.role) return;
    // Turning the link off takes the folder from everyone who came by it, and the link dies for good.
    if (!role && viaLinkCount > 0 && !window.confirm(t("folders.share.link_off_confirm", { count: viaLinkCount }))) {
      return;
    }
    run("link", () => setFolderLink(folderUuid, role), (data) => setAccess(normalizeAccess(data)), "folders.share.link_failed");
  };

  const folderUrl = `${window.location.origin}/questions/folders/${folderUuid}`;
  const inviteUrl = access?.link.token ? `${folderUrl}?invite=${encodeURIComponent(access.link.token)}` : "";

  // navigator.clipboard needs https (or localhost); otherwise a selected textarea and execCommand. The
  // textarea goes inside the dialog: the dialog's focus trap would pull focus back from one in <body>.
  const copyText = async (text) => {
    try {
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // Denied (e.g. no permission): try the old way.
    }
    const container = bodyRef.current || document.body;
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.cssText = "position:absolute;left:-9999px;top:0;opacity:0;";
    const previous = document.activeElement;
    container.appendChild(area);
    area.focus({ preventScroll: true });
    area.select();
    let copied = false;
    try {
      copied = document.execCommand("copy");
    } catch {
      copied = false;
    }
    container.removeChild(area);
    previous?.focus?.({ preventScroll: true });
    return copied;
  };

  const handleCopy = async () => {
    const url = inviteUrl || folderUrl;
    if (await copyText(url)) {
      setManualLink("");
      toast.success(inviteUrl ? t("folders.share.copied_invite") : t("folders.share.copied_link"));
    } else {
      setManualLink(url);
    }
  };

  const close = () => {
    if (busy) return;
    // A person picked but not added would be silently dropped.
    if (candidate && !window.confirm(t("folders.share.discard_pending", { name: candidate.label }))) return;
    onClose();
  };

  const canGrantModerator = !!access?.canGrantModerator;
  const memberRoleOptions = useMemo(
    () => ROLES.filter((role) => role !== "moderator" || canGrantModerator).map((role) => roleOption(t, role)),
    [canGrantModerator, t]
  );
  // A member's own menu: the roles by name (what each allows is told in the add-people select, where a role is
  // chosen), so the whole menu shows without scrolling, "Remove access" last.
  const memberMenuOptions = useMemo(
    () => [
      ...memberRoleOptions.map(({ value, label }) => ({ value, label })),
      { value: REMOVE, label: t("folders.share.remove_access"), danger: true },
    ],
    [memberRoleOptions, t]
  );
  const linkOptions = useMemo(
    () => [
      { value: "", label: t("folders.share.link_off"), hint: t("folders.share.link_off_summary") },
      ...LINK_ROLES.map((role) => roleOption(t, role)),
    ],
    [t]
  );
  const memberIds = useMemo(() => new Set((access?.members || []).map((m) => m.user?.uuid)), [access]);

  const errorAlert = (scope) =>
    error?.scope === scope && (
      <p role="alert" className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
        {getApiErrorMessage(t, error.err, error.fallbackKey)}
      </p>
    );

  // The last title stays while the panel fades out, in case the parent clears `folder` on close.
  const titleRef = useRef("");
  if (folder) titleRef.current = t("folders.share.title", { name: examName ? `${examName} · ${folder.name}` : folder.name });
  const linkRole = access?.link.role || "";

  const renderMember = (member) => {
    const user = member.user || {};
    const name = fullName(user);
    const line = accountLine(user);
    const saving = !!pending[user.uuid];
    // A moderator is changed and removed only by an admin.
    const readOnly = member.role === "moderator" && !canGrantModerator;
    return (
      <li key={user.uuid} data-member={user.uuid} className="py-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <Avatar user={user} />
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-slate-900">
                <span className="min-w-0 break-words">{name}</span>
                {me?.uuid && me.uuid === user.uuid && (
                  <span className="text-xs font-normal text-slate-500">({t("folders.share.you")})</span>
                )}
                {member.via_link && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700 ring-1 ring-inset ring-sky-200">
                    <LinkIcon className="h-3 w-3" aria-hidden="true" />
                    {t("folders.share.via_link")}
                  </span>
                )}
              </p>
              {line && <p className="truncate text-xs text-slate-500">{line}</p>}
              {member.added_by && (
                <p className="truncate text-xs text-slate-400">
                  {t("folders.share.added_by", { name: fullName(member.added_by) })}
                </p>
              )}
            </div>
          </div>
          {/* One control per person: the role, whose menu ends with "Remove access". */}
          <div className="flex items-center gap-2 pl-12 sm:pl-0">
            {readOnly ? (
              <span
                className="inline-flex h-11 min-w-0 flex-1 items-center rounded-lg bg-slate-100 px-3 text-sm text-slate-600 sm:h-10 sm:w-44 sm:flex-none"
                title={t("folders.share.moderator_admin_only")}
              >
                {t("folders.roles.moderator")}
                <span className="sr-only">. {t("folders.share.moderator_admin_only")}</span>
              </span>
            ) : (
              <RoleSelect
                className="min-w-0 flex-1 sm:w-44 sm:flex-none"
                ariaLabel={t("folders.share.role_of", { name })}
                options={memberMenuOptions}
                value={member.role}
                onChange={(role) => (role === REMOVE ? handleRemove(member) : handleRoleChange(member, role))}
                isDisabled={saving}
                isLoading={saving}
              />
            )}
          </div>
        </div>
        {errorAlert(user.uuid)}
      </li>
    );
  };

  const renderBody = () => {
    if (!access && (loading || !loadError)) {
      return (
        <div role="status" className="flex flex-col items-center justify-center gap-3 py-14 text-sm text-slate-500">
          <Spinner className="h-6 w-6 text-blue-600" />
          {t("folders.share.loading")}
        </div>
      );
    }
    if (!access) {
      return (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {getApiErrorMessage(t, loadError, "folders.share.load_failed")}
          </p>
          <Button
            type="button"
            color="secondary-outline"
            Icon={ArrowPathIcon}
            text={t("folders.share.retry")}
            onClick={() => loadAccess(folderUuid)}
          />
        </div>
      );
    }

    return (
      <>
        {/* Add people */}
        <section className="mt-4">
          <label htmlFor={`${id}-search`} className="block text-sm font-semibold text-slate-900">
            {t("folders.share.add_title")}
          </label>
          {/* Phones: the search, then the role and "Add" in one row. */}
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-start">
            <div className="min-w-0 flex-1">
              <AsyncSelect
                key={searchKey}
                inputId={`${id}-search`}
                styles={searchSelectStyles}
                theme={selectTheme}
                defaultOptions
                loadOptions={loadCandidates}
                // The server searches name, login and e-mail; here only people added meanwhile are hidden.
                filterOption={(option) => !memberIds.has(option.value)}
                value={candidate}
                onChange={(option) => {
                  setCandidate(option);
                  if (error?.scope === "add") setError(null);
                }}
                formatOptionLabel={(option, { context }) =>
                  context === "value" ? (
                    option.label
                  ) : (
                    <span className="flex min-w-0 items-center gap-2.5">
                      <Avatar user={option.user} small />
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{option.label}</span>
                        {accountLine(option.user) && (
                          <span className="block truncate text-xs opacity-75">{accountLine(option.user)}</span>
                        )}
                      </span>
                    </span>
                  )
                }
                isClearable
                isDisabled={!!pending.add}
                placeholder={t("folders.share.search_placeholder")}
                noOptionsMessage={({ inputValue }) =>
                  searchFailed
                    ? t("folders.share.search_failed")
                    : inputValue.trim()
                    ? t("folders.share.no_candidates")
                    : t("folders.share.type_to_search")
                }
                loadingMessage={() => t("table.loading")}
              />
            </div>
            <div className="flex gap-2">
              <RoleSelect
                className="min-w-0 flex-1 sm:w-44 sm:flex-none"
                ariaLabel={t("folders.share.new_role_label")}
                options={memberRoleOptions}
                value={newRole}
                onChange={setNewRole}
                isDisabled={!!pending.add}
              />
              <Button
                type="button"
                color="primary"
                Icon={UserPlusIcon}
                text={t("buttons.add")}
                className="shrink-0 max-sm:h-11"
                onClick={handleAdd}
                loading={!!pending.add}
                disabled={!candidate}
              />
            </div>
          </div>
          {errorAlert("add")}
        </section>

        {/* People with access */}
        <section className="mt-6" aria-labelledby={`${id}-members`}>
          <h4 id={`${id}-members`} className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            {t("folders.share.members_title")}
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium tabular-nums text-slate-600">
              {access.members.length}
            </span>
          </h4>
          {access.members.length ? (
            <ul ref={membersRef} className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200 px-3 sm:px-4">
              {access.members.map(renderMember)}
            </ul>
          ) : (
            <p className="mt-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center text-sm text-slate-500">
              {t("folders.share.members_empty")}
            </p>
          )}
          {viaLinkCount > 0 && <p className="mt-2 text-xs text-slate-500">{t("folders.share.via_link_hint")}</p>}
        </section>

        {/* Anyone with the link */}
        <section className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <span
                className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                  linkRole ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-500"
                }`}
              >
                <LinkIcon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <label htmlFor={`${id}-link`} className="block text-sm font-semibold text-slate-900">
                  {t("folders.share.link_title")}
                </label>
                <p className="text-xs text-slate-500">
                  {linkRole
                    ? t("folders.share.link_on_summary", { role: t(`folders.roles.${linkRole}`) })
                    : t("folders.share.link_off_summary")}
                </p>
              </div>
            </div>
            <RoleSelect
              inputId={`${id}-link`}
              className="sm:w-44"
              options={linkOptions}
              value={linkRole}
              onChange={handleLinkChange}
              isDisabled={!!pending.link}
              isLoading={!!pending.link}
            />
          </div>
          <p className="mt-2 text-xs leading-relaxed text-slate-500">{t("folders.share.link_hint")}</p>
          {errorAlert("link")}
        </section>

        {manualLink && (
          <div className="mt-4">
            <label htmlFor={`${id}-manual`} className="mb-1 block text-sm text-red-700">
              {t("folders.share.copy_failed")}
            </label>
            <input
              id={`${id}-manual`}
              type="text"
              readOnly
              value={manualLink}
              onFocus={(e) => e.target.select()}
              className="block h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/15"
            />
          </div>
        )}
      </>
    );
  };

  return (
    <CustomDialog isOpen={isOpen} closeModal={close} headerText={titleRef.current} maxWidth="max-w-2xl">
      {/* Esc closes the dialog from a select too, while its list is closed (react-select keeps that Esc). */}
      <div
        ref={bodyRef}
        className="relative"
        onKeyDownCapture={(e) => {
          if (e.key === "Escape" && e.target.getAttribute?.("aria-expanded") === "false" && !e.target.value) close();
        }}
      >
        {renderBody()}
        {/* One row: copying the link is a quiet action, "Done" the main one. */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4">
          <button
            type="button"
            onClick={handleCopy}
            disabled={!access}
            className={`-ml-2 inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-blue-700 transition duration-200 enabled:hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-[2.25rem] ${focusRing}`}
          >
            <LinkIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            {inviteUrl ? t("folders.share.copy_invite") : t("folders.share.copy_link")}
          </button>
          <Button type="button" color="primary" text={t("folders.share.done")} onClick={close} disabled={busy} className="max-sm:h-11" />
        </div>
      </div>
    </CustomDialog>
  );
}
